import React from "react";
import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  Select,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import StatusIcon from "../../../../../shared/components/StatusLabel/StatusIcon";
import TemplatesModal from "./components/ChoiceTemplateModal/TemplatesModal";

import { useChannelsData } from "../../../hooks/useChannelsData";
import { useTemplatesData } from "../../../hooks/useTemplatesData";

/* =========================
   CONFIG
========================= */

const CHANNELS_CONFIG = [
  {
    id: "whatsapp",
    sendField: "sendWhatsapp",
    statusLabel: "WhatsApp",
    channelField: "whatsappChannelId",
    modelField: "whatsappTemplateId",
    templateTypeLabel: "WhatsApp",
    templateType: "whatsapp",
  },
  {
    id: "sms",
    sendField: "sendSms",
    statusLabel: "SMS",
    channelField: "smsChannelId",
    modelField: "smsTemplateId",
    templateTypeLabel: "SMS",
    templateType: "sms",
  },
  {
    id: "email",
    sendField: "sendEmail",
    statusLabel: "Email",
    channelField: "emailChannelId",
    modelField: "emailTemplateId",
    templateTypeLabel: "E-mail",
    templateType: "email",
  },
];

/* =========================
  Normaliza type do CHANNEL
========================= */
function normalizeChannelType(type) {
  if (!type) return null;

  if (type === "whatsapp_api") return "whatsapp";
  if (type === "whatsapp_web") return "whatsapp";
  if (type === "sms") return "sms";
  if (type === "email") return "email";

  return null;
}


/* =========================
  Normaliza type do TEMPLATE
========================= */
function normalizeTemplateType(tpl) {
  const raw = tpl?.channel_type || tpl?.type;

  if (!raw) return null;

  if (raw === "whatsapp_api") return "whatsapp_api";
  if (raw === "whatsapp_web") return "whatsapp_web";
  if (raw === "sms") return "sms";
  if (raw === "email") return "email";

  return null;
}

export function StepChannelsTemplates({
  data,
  updateData,
  setIsValid,
  showErrors,
}) {
  const theme = useTheme();

  const { channels, loading: loadingChannels } = useChannelsData();
  const { templates, loading: loadingTemplates } = useTemplatesData();

  const isLoadingChannels = loadingChannels;
  const isLoadingTemplates = loadingTemplates;

  /* =========================
    Agrupa canais por tipo
  ========================= */

  const groupedChannels = React.useMemo(() => {
    if (!Array.isArray(channels)) return {};

    return channels.reduce((acc, ch) => {
      const normalizedType = normalizeChannelType(ch.type);
      if (!normalizedType) return acc;

      if (!acc[normalizedType]) acc[normalizedType] = [];

      acc[normalizedType].push({
        id: ch.id,
        title: ch.instance_name || ch.sender || ch.provider || "Canal sem nome",
        raw: ch,
      });

      return acc;
    }, {});
  }, [channels]);

  /* =========================
     Modal Templates
  ========================= */

  const [open, setOpen] = React.useState(false);
  const [field, setField] = React.useState(null);
  const [templateType, setTemplateType] = React.useState("");
  const [dataToTable, setDataToTable] = React.useState([]);

  function getSelectedChannelType(channelId) {
    const allChannels = Array.isArray(channels) ? channels : [];
    const found = allChannels.find((c) => c.id === channelId);
    return found?.type || null;
  }

  const openTemplatesModal = ({ modelField, channelField }) => {
    const selectedChannelId = data?.[channelField];

    if (!selectedChannelId) return;

    const selectedType = getSelectedChannelType(selectedChannelId);

    setOpen(true);
    setField(modelField);
    setTemplateType(selectedType);

    const safeTemplates = Array.isArray(templates) ? templates : [];

    const filtered = safeTemplates.filter((tpl) => {
      return normalizeTemplateType(tpl) === selectedType;
    });

    setDataToTable(filtered);
  };

  /* =========================
     Handlers
  ========================= */

  const toggleSend = (sendField, channelField, modelField) => {
    const nextEnabled = data?.[sendField] !== true;

    updateData({
      [sendField]: nextEnabled,
      ...(!nextEnabled ? { [channelField]: "", [modelField]: "" } : {}),
    });
  };

  const handleChannelChange = (channelField, modelField, value) => {
    updateData({ [channelField]: value, [modelField]: "" });
  };

  /* =========================
     Validação
  ========================= */

  const enabledChannels = CHANNELS_CONFIG.filter(
    (c) => data?.[c.sendField] === true,
  );

  const hasAtLeastOneChannel = enabledChannels.length > 0;

  const allEnabledChannelsComplete = enabledChannels.every((c) => {
    return Boolean(data?.[c.channelField]) && Boolean(data?.[c.modelField]);
  });

  const stepIsValid = hasAtLeastOneChannel && allEnabledChannelsComplete;

  React.useEffect(() => {
    setIsValid(stepIsValid);
  }, [stepIsValid, setIsValid]);

  /* =========================
     GRID DESKTOP FIXO
  ========================= */

  const desktopGridSx = {
    display: "grid",
    gridTemplateColumns: "56px 180px 260px 260px",
    columnGap: 16,
    alignItems: "start",
    width: "100%",
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        mt: 4,
        width: "min(100%, 920px)",
        px: { xs: 2, md: 0 },
      }}
    >
      {/* HEADER DESKTOP */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Box sx={desktopGridSx}>
          <Typography fontSize={14} color="text.secondary">
            Enviar
          </Typography>
          <Typography fontSize={14} color="text.secondary">
            Tipo
          </Typography>
          <Typography fontSize={14} color="text.secondary">
            Canal
          </Typography>
          <Typography fontSize={14} color="text.secondary">
            Modelo
          </Typography>
        </Box>
      </Box>

      {/* ROWS */}
      {CHANNELS_CONFIG.map((channel) => {
        const enabled = data?.[channel.sendField] === true;
        const channelId = data?.[channel.channelField] || "";
        const templateId = data?.[channel.modelField] || "";

        const shouldShowChannelError = showErrors && enabled && !channelId;
        const shouldShowTemplateError = showErrors && enabled && !templateId;

        const channelOptions = groupedChannels[channel.id] || [];

        return (
          <Box key={channel.id} sx={{ mb: { xs: 2, md: 0 } }}>
            {/* DESKTOP */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Box sx={desktopGridSx}>
                <Checkbox
                  checked={enabled}
                  onChange={() =>
                    toggleSend(
                      channel.sendField,
                      channel.channelField,
                      channel.modelField,
                    )
                  }
                />

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <StatusIcon status={channel.statusLabel} />
                  <Typography>{channel.statusLabel}</Typography>
                </Box>

                {/* Canal */}
                <Box>
                  <Box sx={{ position: "relative" }}>
                    <Select
                      fullWidth
                      disabled={!enabled || isLoadingChannels}
                      value={channelId}
                      onChange={(e) =>
                        handleChannelChange(
                          channel.channelField,
                          channel.modelField,
                          e.target.value,
                        )
                      }
                      error={shouldShowChannelError}
                      size="small"
                      sx={{
                        ...(isLoadingChannels && {
                          "& .MuiSelect-icon": {
                            display: "none",
                          },
                        }),
                      }}
                    >
                      <MenuItem value="" disabled>
                        {isLoadingChannels
                          ? "Carregando canais..."
                          : "Selecionar canal"}
                      </MenuItem>

                      {channelOptions.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.title}
                        </MenuItem>
                      ))}
                    </Select>

                    {isLoadingChannels && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Box>

                  {shouldShowChannelError && (
                    <Typography color="error" fontSize={12} mt={0.5}>
                      Selecione um canal
                    </Typography>
                  )}
                </Box>

                {/* Modelo */}
                <Box>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={!enabled || isLoadingTemplates}
                    onClick={() =>
                      openTemplatesModal({
                        modelField: channel.modelField,
                        channelField: channel.channelField,
                      })
                    }
                    sx={{
                      ...(shouldShowTemplateError && {
                        border: `1px solid ${theme.palette.error.main}`,
                      }),
                    }}
                  >
                    {templateId
                      ? "Modelo selecionado!"
                      : isLoadingTemplates
                        ? "Carregando modelos..."
                        : "Selecionar Modelo"}
                  </Button>

                  {shouldShowTemplateError && (
                    <Typography color="error" fontSize={12} mt={0.5}>
                      Selecione um modelo
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* MOBILE */}
            <Box
              sx={{
                display: { xs: "block", md: "none" },
                background: "#fff",
                borderRadius: 2,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
              }}
            >
              <Stack spacing={2}>
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <StatusIcon status={channel.statusLabel} />
                    <Typography fontWeight={600}>
                      {channel.statusLabel}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontSize={13} color="text.secondary">
                      Habilitar
                    </Typography>

                    <Checkbox
                      checked={enabled}
                      onChange={() =>
                        toggleSend(
                          channel.sendField,
                          channel.channelField,
                          channel.modelField,
                        )
                      }
                    />
                  </Box>
                </Box>

                {/* Canal */}
                <Box sx={{ position: "relative" }}>
                  <Select
                    fullWidth
                    disabled={!enabled || isLoadingChannels}
                    value={channelId}
                    onChange={(e) =>
                      handleChannelChange(
                        channel.channelField,
                        channel.modelField,
                        e.target.value,
                      )
                    }
                    error={shouldShowChannelError}
                    size="small"
                    sx={{
                      ...(isLoadingChannels && {
                        "& .MuiSelect-icon": {
                          display: "none",
                        },
                      }),
                    }}
                  >
                    <MenuItem value="" disabled>
                      {isLoadingChannels
                        ? "Carregando canais..."
                        : "Selecionar canal"}
                    </MenuItem>

                    {channelOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>

                  {isLoadingChannels && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </Box>

                {shouldShowChannelError && (
                  <Typography color="error" fontSize={12}>
                    Selecione um canal
                  </Typography>
                )}

                {/* Modelo */}
                <Button
                  fullWidth
                  variant="contained"
                  disabled={!enabled || isLoadingTemplates}
                  onClick={() =>
                    openTemplatesModal({
                      modelField: channel.modelField,
                      channelField: channel.channelField,
                    })
                  }
                  sx={{
                    ...(shouldShowTemplateError && {
                      border: `1px solid ${theme.palette.error.main}`,
                    }),
                  }}
                >
                  {templateId
                    ? "Modelo selecionado!"
                    : isLoadingTemplates
                      ? "Carregando modelos..."
                      : "Selecionar Modelo"}
                </Button>

                {shouldShowTemplateError && (
                  <Typography color="error" fontSize={12}>
                    Selecione um modelo
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
        );
      })}

      {showErrors && !hasAtLeastOneChannel && (
        <Typography color="error" fontSize={12}>
          Selecione pelo menos 1 canal para continuar.
        </Typography>
      )}

      <TemplatesModal
        open={open}
        onClose={() => setOpen(false)}
        templateOptions={dataToTable}
        templateType={templateType}
        updateFieldValue={(fieldName, value) =>
          updateData({ [fieldName]: value })
        }
        setError={() => {}}
        field={field}
        field_id={field ? data?.[field] : ""}
        loading={isLoadingTemplates}
      />
    </Box>
  );
}
